/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * ImageBannerEditor — full-featured Image Banner configuration panel.
 * 5 tabs: Image (source, fit, dimensions), Overlay (overlay, text overlay),
 * Buttons (CTA, link & interaction), Style (border, spacing, caption),
 * Effects (animation, responsive).
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTheme } from '../../../theme/ThemeContext';

// ─── Default config ───────────────────────────────────────────────────────────

export const DEFAULT_IMAGE_BANNER_CONFIG = {
  // Image
  imageUrl: '',
  imageAlt: '',
  imageFit: 'cover',
  imagePosition: 'center',
  imagePositionX: 50,
  imagePositionY: 50,
  imageRepeat: 'no-repeat',
  imageParallax: false,
  imageParallaxSpeed: 0.4,
  imageLazyLoad: true,
  imageHoverZoom: false,
  imageHoverZoomScale: 1.08,
  imageHoverTransitionDuration: 400,

  // Banner dimensions
  width: 'full',
  height: 'auto',
  heightPx: 400,
  heightMobile: 220,
  heightTablet: 320,
  aspectRatio: 'none',
  customAspectRatioX: 16,
  customAspectRatioY: 9,
  maxWidth: 'none',
  maxWidthPx: 1400,

  // Overlay
  overlayVisible: false,
  overlayType: 'solid',
  overlayColor: '#000000',
  overlayOpacity: 40,
  overlayGradientFrom: '#000000',
  overlayGradientTo: '#7c3aed',
  overlayGradientAngle: 135,
  overlayGradientType: 'linear',
  overlayBlendMode: 'normal',

  // Text overlay content
  textOverlayVisible: false,
  textOverlayLayout: 'centered',
  textOverlayPositionX: 50,
  textOverlayPositionY: 50,
  badgeVisible: false,
  badgeText: 'New',
  badgeColor: '#7c3aed',
  headlineText: 'Your headline here',
  headlineFontSize: 40,
  headlineFontWeight: 700,
  headlineFontFamily: '',
  headlineColor: '#ffffff',
  subheadlineVisible: true,
  subheadlineText: 'A short supporting sentence.',
  subheadlineFontSize: 18,
  subheadlineColor: '#e2e8f0',
  textMaxWidth: 640,
  textPadding: 24,
  textAlignment: 'center',
  textShadow: false,
  textShadowColor: '#000000',
  textShadowBlur: 8,

  // CTA
  ctaVisible: false,
  primaryCTA: { label: 'Get Started', url: '/sign-up', openNewTab: false, style: 'filled', color: '#7c3aed', textColor: '#ffffff', size: 'md', icon: '', iconPosition: 'left' },
  secondaryCTA: { visible: false, label: 'Learn More', url: '#', openNewTab: false, style: 'outlined', color: '#ffffff', textColor: '#ffffff', size: 'md' },
  ctaLayout: 'side-by-side',
  ctaMarginTop: 24,

  // Border & shape
  borderVisible: false,
  borderColor: '#e2e8f0',
  borderWidth: 1,
  borderStyle: 'solid',
  borderRadius: 0,
  borderRadiusPerCorner: false,
  borderRadiusTopLeft: 0,
  borderRadiusTopRight: 0,
  borderRadiusBottomLeft: 0,
  borderRadiusBottomRight: 0,
  boxShadow: 'none',
  boxShadowColor: '#000000',
  boxShadowX: 0,
  boxShadowY: 8,
  boxShadowBlur: 24,
  boxShadowSpread: 0,
  shapeDividerBottom: 'none',
  shapeDividerColor: '#ffffff',
  shapeDividerHeight: 60,
  clipPath: 'none',
  clipPathCustomValue: '',

  // Spacing & layout
  marginTop: 0,
  marginBottom: 0,
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
  sectionWidth: 'full-bleed',

  // Link & interaction
  bannerLinkUrl: '',
  bannerLinkOpenNewTab: false,
  cursorStyle: 'default',
  hoverOverlayColor: '#000000',
  hoverOverlayOpacity: 0,
  hoverScale: 1.0,
  hoverTransitionDuration: 300,
  lightboxEnabled: false,

  // Caption & credit
  captionVisible: false,
  captionText: '',
  captionPosition: 'below',
  captionFontSize: 13,
  captionColor: '#64748b',
  captionAlignment: 'center',
  creditVisible: false,
  creditText: '',
  creditUrl: '',

  // Animation
  entranceAnimation: 'none',
  animationDelay: 0,
  animationDuration: 600,
  animationTrigger: 'on-scroll',

  // Responsive
  visibleOnDesktop: true,
  visibleOnTablet: true,
  visibleOnMobile: true,
  mobileImageUrl: '',
  tabletImageUrl: '',

  // SEO & accessibility
  ariaLabel: '',
  role: 'img',
  id: '',
  customClassName: '',
};

// ─── Content keys preserved during preset apply ───────────────────────────────

const CONTENT_KEYS = [
  'imageUrl', 'imageAlt', 'mobileImageUrl', 'tabletImageUrl',
  'headlineText', 'subheadlineText', 'badgeText',
  'captionText', 'creditText', 'creditUrl',
  'bannerLinkUrl',
  'primaryCTA', 'secondaryCTA',
];

// ─── Presets ──────────────────────────────────────────────────────────────────

const PRESETS = [
  {
    key: 'hero_fullbleed',
    label: 'Full-bleed Hero',
    icon: 'bi-fullscreen',
    description: 'Full viewport height, centered text, dark overlay',
    config: {
      height: 'viewport', aspectRatio: 'none', sectionWidth: 'full-bleed',
      overlayVisible: true, overlayType: 'solid', overlayColor: '#000000', overlayOpacity: 50,
      textOverlayVisible: true, textOverlayLayout: 'centered',
      headlineFontSize: 52, headlineFontWeight: 800, headlineColor: '#ffffff',
      subheadlineVisible: true, subheadlineFontSize: 20, subheadlineColor: '#e2e8f0',
      textAlignment: 'center', textShadow: true,
      ctaVisible: true,
      imageFit: 'cover', imagePosition: 'center',
    },
  },
  {
    key: 'promo_banner',
    label: 'Promotional Banner',
    icon: 'bi-megaphone',
    description: '16:9 ratio, bottom-left text, CTA button',
    config: {
      aspectRatio: '16:9', height: 'auto', sectionWidth: 'full-bleed',
      overlayVisible: true, overlayType: 'gradient', overlayGradientFrom: 'rgba(0,0,0,0)', overlayGradientTo: '#000000', overlayGradientAngle: 0, overlayOpacity: 70,
      textOverlayVisible: true, textOverlayLayout: 'bottom-left',
      headlineFontSize: 36, headlineFontWeight: 700, headlineColor: '#ffffff',
      subheadlineVisible: true, subheadlineFontSize: 16, subheadlineColor: '#e2e8f0',
      textAlignment: 'left',
      ctaVisible: true,
      imageFit: 'cover',
    },
  },
  {
    key: 'minimal_product',
    label: 'Minimal Product',
    icon: 'bi-image',
    description: 'No overlay, no text, contained with soft radius',
    config: {
      height: 'auto', aspectRatio: '4:3', sectionWidth: 'contained',
      overlayVisible: false, textOverlayVisible: false, ctaVisible: false,
      borderVisible: false, borderRadius: 12,
      boxShadow: 'lg',
      imageFit: 'contain', imageRepeat: 'no-repeat',
    },
  },
  {
    key: 'split_content',
    label: 'Split Content',
    icon: 'bi-layout-split',
    description: '45% height, diagonal clip-path, gradient overlay',
    config: {
      heightPx: 420, height: 'px', sectionWidth: 'full-bleed', aspectRatio: 'none',
      clipPath: 'diagonal-right',
      overlayVisible: true, overlayType: 'gradient', overlayGradientFrom: '#7c3aed', overlayGradientTo: '#2563eb', overlayGradientAngle: 135, overlayOpacity: 80,
      textOverlayVisible: true, textOverlayLayout: 'centered',
      headlineFontSize: 38, headlineFontWeight: 700, headlineColor: '#ffffff',
      subheadlineVisible: true, subheadlineFontSize: 17, subheadlineColor: '#ddd6fe',
      ctaVisible: true, imageFit: 'cover',
    },
  },
  {
    key: 'announcement_strip',
    label: 'Announcement Strip',
    icon: 'bi-info-circle',
    description: 'Short, solid overlay, centered small text + CTA',
    config: {
      heightPx: 140, height: 'px', sectionWidth: 'full-bleed', aspectRatio: 'none',
      overlayVisible: true, overlayType: 'solid', overlayColor: '#1e293b', overlayOpacity: 92,
      textOverlayVisible: true, textOverlayLayout: 'centered',
      headlineFontSize: 22, headlineFontWeight: 600, headlineColor: '#ffffff',
      subheadlineVisible: false, textAlignment: 'center',
      ctaVisible: true,
    },
  },
  {
    key: 'bg_texture',
    label: 'Background Texture',
    icon: 'bi-grid-3x3',
    description: 'Repeating pattern, no text, section bg use',
    config: {
      height: 'auto', heightPx: 200, aspectRatio: 'none', sectionWidth: 'full-bleed',
      imageFit: 'none', imageRepeat: 'repeat', imagePosition: 'top',
      overlayVisible: false, textOverlayVisible: false, ctaVisible: false,
    },
  },
];

// ─── Tabs ────────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'image',   label: 'Image',   icon: 'bi-image' },
  { key: 'overlay', label: 'Overlay', icon: 'bi-layers' },
  { key: 'buttons', label: 'Buttons', icon: 'bi-cursor' },
  { key: 'style',   label: 'Style',   icon: 'bi-pentagon' },
  { key: 'effects', label: 'Effects', icon: 'bi-stars' },
];

// ─── Google Fonts ─────────────────────────────────────────────────────────────

const GOOGLE_FONTS = [
  'Albert Sans','Anton','Archivo','Barlow','Barlow Condensed','Bebas Neue',
  'Cabin','Caveat','Chakra Petch','Comfortaa','Cormorant Garamond',
  'Crimson Text','Dancing Script','DM Mono','DM Sans','DM Serif Display',
  'EB Garamond','Exo 2','Figtree','Fira Code','Fira Sans','Fraunces',
  'Great Vibes','IBM Plex Mono','IBM Plex Sans','Indie Flower','Inter',
  'JetBrains Mono','Josefin Sans','Jost','Kanit','Lato','Lexend',
  'Libre Baskerville','Lora','Manrope','Merriweather','Montserrat',
  'Mulish','Noto Sans','Noto Serif','Nunito','Open Sans','Oswald',
  'Outfit','Pacifico','Playfair Display','Plus Jakarta Sans','Poppins',
  'Quicksand','Raleway','Righteous','Roboto','Roboto Mono','Rubik',
  'Russo One','Sacramento','Satisfy','Sora','Source Code Pro',
  'Source Sans 3','Source Serif 4','Space Grotesk','Space Mono',
  'Spectral','Syne','Teko','Ubuntu','Urbanist','Work Sans',
].sort((a, b) => a.localeCompare(b));

// ─── Primitive controls ───────────────────────────────────────────────────────

function Toggle({ value, onChange }) {
  const { theme } = useTheme();
  return (
    <div role="switch" aria-checked={value} onClick={() => onChange(!value)}
      style={{ width: 36, height: 20, borderRadius: 10, background: value ? theme.primary : '#cbd5e1', position: 'relative', cursor: 'pointer', transition: 'background 0.18s', flexShrink: 0 }}>
      <div style={{ position: 'absolute', top: 2, left: value ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.18s', boxShadow: '0 1px 3px rgba(0,0,0,0.25)' }} />
    </div>
  );
}

function Seg({ value, onChange, options }) {
  const { theme } = useTheme();
  return (
    <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #e2e8f0', width: '100%' }}>
      {options.map((opt, i) => {
        const active = value === opt.value;
        return (
          <button key={opt.value} type="button" title={opt.title || opt.label} onClick={() => onChange(opt.value)}
            style={{ flex: 1, padding: '5px 4px', border: 'none', borderLeft: i > 0 ? '1px solid #e2e8f0' : 'none', background: active ? theme.primary : '#f8fafc', color: active ? '#fff' : '#64748b', fontSize: 11, fontWeight: active ? 600 : 400, cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
            {opt.icon && <i className={`bi ${opt.icon}`} style={{ fontSize: 11 }} />}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function ColorField({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input type="color" value={value || '#000000'} onChange={e => onChange(e.target.value)}
        style={{ width: 32, height: 28, padding: 2, border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }} />
      <input className="form-control form-control-sm" value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder="#000000" style={{ fontFamily: 'monospace', fontSize: 12 }} />
    </div>
  );
}

function NumInput({ value, onChange, min = 0, max = 9999, step = 1, unit = '' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <input type="number" className="form-control form-control-sm" value={value ?? 0}
        onChange={e => onChange(Number(e.target.value))} min={min} max={max} step={step}
        style={{ fontSize: 12, textAlign: 'center', width: 70 }} />
      {unit && <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>{unit}</span>}
    </div>
  );
}

function RangeSlider({ value, onChange, min = 0, max = 100, step = 1, unit = '' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input type="range" min={min} max={max} step={step} value={value ?? 0}
        onChange={e => onChange(Number(e.target.value))}
        style={{ flex: 1, accentColor: 'var(--bs-primary)' }} />
      <span style={{ fontSize: 11, color: '#64748b', minWidth: 32, textAlign: 'right' }}>{value}{unit}</span>
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
        {label}
        {hint && <span title={hint} style={{ cursor: 'help', color: '#94a3b8', fontSize: 10 }}><i className="bi bi-info-circle" /></span>}
      </div>
      {children}
    </div>
  );
}

function InlineRow({ label, hint, children }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#475569', display: 'flex', alignItems: 'center', gap: 4 }}>
        {label}
        {hint && <span title={hint} style={{ cursor: 'help', color: '#94a3b8', fontSize: 10 }}><i className="bi bi-info-circle" /></span>}
      </span>
      {children}
    </div>
  );
}

function SectionDivider({ label }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', margin: '12px 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
      {label}
      <div style={{ flex: 1, height: 1, background: '#f1f5f9' }} />
    </div>
  );
}

function SubPanel({ children }) {
  return (
    <div style={{ padding: '8px 10px', background: '#f8fafc', borderRadius: 6, border: '1px solid #f1f5f9', marginBottom: 8 }}>
      {children}
    </div>
  );
}

function Accordion({ title, icon, open, onToggle, children, badge }) {
  const { theme } = useTheme();
  return (
    <div style={{ marginBottom: 2 }}>
      <button type="button" onClick={onToggle}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 7, padding: '9px 10px', border: `1px solid ${open ? theme.primary + '30' : '#e2e8f0'}`, borderRadius: open ? '6px 6px 0 0' : 6, background: open ? theme.primary + '08' : '#fafafa', cursor: 'pointer', textAlign: 'left' }}>
        {icon && <i className={`bi ${icon}`} style={{ fontSize: 13, color: open ? theme.primary : '#64748b', flexShrink: 0 }} />}
        <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: open ? theme.primary : '#374151' }}>{title}</span>
        {badge && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 10, background: theme.primary + '18', color: theme.primary }}>{badge}</span>}
        <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: 10, color: '#94a3b8' }} />
      </button>
      {open && (
        <div style={{ padding: '12px 10px 4px', border: `1px solid ${theme.primary}18`, borderTop: 'none', borderRadius: '0 0 6px 6px', background: '#fff', marginBottom: 2 }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ─── FontPicker ───────────────────────────────────────────────────────────────

function FontPicker({ value, onChange }) {
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const filtered = GOOGLE_FONTS.filter(f => f.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) { setOpen(false); setSearch(''); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  useEffect(() => { if (open && searchRef.current) searchRef.current.focus(); }, [open]);

  const select = (font) => { onChange(font); setOpen(false); setSearch(''); };
  const clear  = ()      => { onChange('');   setOpen(false); setSearch(''); };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 12, gap: 6, color: value ? '#1e293b' : '#9ca3af', outline: open ? `2px solid ${theme.primary}40` : 'none' }}>
        <span style={{ fontFamily: value ? `"${value}", sans-serif` : 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || 'Default (inherit)'}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {value && <span role="button" onClick={e => { e.stopPropagation(); clear(); }} style={{ color: '#94a3b8', lineHeight: 1, padding: '0 2px', cursor: 'pointer' }}><i className="bi bi-x" style={{ fontSize: 13 }} /></span>}
          <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: 10, color: '#94a3b8' }} />
        </div>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 3px)', left: 0, right: 0, zIndex: 200, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 28px rgba(0,0,0,0.14)', overflow: 'hidden' }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ position: 'relative' }}>
              <i className="bi bi-search" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#94a3b8', pointerEvents: 'none' }} />
              <input ref={searchRef} className="form-control form-control-sm" placeholder="Search fonts…" value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape') { setOpen(false); setSearch(''); } if (e.key === 'Enter' && filtered.length > 0) select(filtered[0]); }}
                style={{ paddingLeft: 26, fontSize: 12 }} />
            </div>
          </div>
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            <button type="button" onClick={clear}
              style={{ width: '100%', padding: '7px 12px', border: 'none', borderBottom: '1px solid #f1f5f9', background: !value ? `${theme.primary}10` : '#fff', textAlign: 'left', fontSize: 11, color: '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <i className="bi bi-x-circle" style={{ fontSize: 11 }} /> Default (no custom font)
            </button>
            {filtered.map(font => {
              const active = value === font;
              return (
                <button key={font} type="button" onClick={() => select(font)}
                  style={{ width: '100%', padding: '7px 12px', border: 'none', borderBottom: '1px solid #f8fafc', background: active ? `${theme.primary}10` : '#fff', textAlign: 'left', fontSize: 13, fontFamily: `"${font}", sans-serif`, color: active ? theme.primary : '#374151', fontWeight: active ? 700 : 400, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = '#fff'; }}>
                  {font}
                  {active && <i className="bi bi-check2" style={{ fontSize: 12, color: theme.primary }} />}
                </button>
              );
            })}
          </div>
          <div style={{ padding: '6px 10px', borderTop: '1px solid #f1f5f9', fontSize: 10, color: '#94a3b8', textAlign: 'center' }}>
            {filtered.length} font{filtered.length !== 1 ? 's' : ''} • loads automatically from Google Fonts
          </div>
        </div>
      )}
    </div>
  );
}

// ─── WeightSelector ───────────────────────────────────────────────────────────

const WEIGHTS = [100, 200, 300, 400, 500, 600, 700, 800, 900];
const WEIGHT_LABEL = { 100: 'Thin', 200: 'ExLight', 300: 'Light', 400: 'Regular', 500: 'Medium', 600: 'SemiBold', 700: 'Bold', 800: 'ExBold', 900: 'Black' };

function WeightSelector({ value, onChange }) {
  const { theme } = useTheme();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
      {WEIGHTS.map(w => {
        const active = value === w;
        return (
          <button key={w} type="button" onClick={() => onChange(w)} title={WEIGHT_LABEL[w]}
            style={{ padding: '5px 4px', border: `1px solid ${active ? theme.primary : '#e2e8f0'}`, borderRadius: 5, background: active ? `${theme.primary}10` : '#fff', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <span style={{ fontWeight: w, fontSize: 14, color: active ? theme.primary : '#374151', lineHeight: 1 }}>Aa</span>
            <span style={{ fontSize: 9, color: active ? theme.primary : '#94a3b8' }}>{w}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Image position picker (3×3 grid) ────────────────────────────────────────

const POS_GRID = [
  ['top left',    'top center',    'top right'],
  ['center left', 'center',        'center right'],
  ['bottom left', 'bottom center', 'bottom right'],
];
const POS_LABEL_MAP = {
  'top left': 'top', 'top center': 'top', 'top right': 'top',
  'center left': 'left', 'center': 'center', 'center right': 'right',
  'bottom left': 'bottom', 'bottom center': 'bottom', 'bottom right': 'bottom',
};

function ImagePositionGrid({ value, onSelect }) {
  const { theme } = useTheme();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 3, width: 90 }}>
      {POS_GRID.flat().map(pos => {
        const active = value === pos || (value === 'center' && pos === 'center');
        return (
          <button key={pos} type="button" title={pos} onClick={() => onSelect(pos)}
            style={{ width: 26, height: 26, border: `2px solid ${active ? theme.primary : '#e2e8f0'}`, borderRadius: 4, background: active ? `${theme.primary}18` : '#f8fafc', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: active ? theme.primary : '#cbd5e1' }} />
          </button>
        );
      })}
    </div>
  );
}

// ─── Overlay preview swatch ───────────────────────────────────────────────────

function OverlaySwatch({ cfg }) {
  const overlayStyle = cfg.overlayType === 'gradient'
    ? { background: cfg.overlayGradientType === 'radial'
        ? `radial-gradient(circle, ${cfg.overlayGradientFrom}, ${cfg.overlayGradientTo})`
        : `linear-gradient(${cfg.overlayGradientAngle}deg, ${cfg.overlayGradientFrom}, ${cfg.overlayGradientTo})`,
        opacity: cfg.overlayOpacity / 100 }
    : { background: cfg.overlayColor, opacity: cfg.overlayOpacity / 100 };

  return (
    <div style={{ position: 'relative', width: '100%', height: 48, borderRadius: 6, overflow: 'hidden', border: '1px solid #e2e8f0', marginBottom: 10 }}>
      <div style={{ position: 'absolute', inset: 0, background: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8'%3E%3Crect width='4' height='4' fill='%23ccc'/%3E%3Crect x='4' y='4' width='4' height='4' fill='%23ccc'/%3E%3C/svg%3E") repeat`, backgroundSize: '8px 8px' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #7c3aed 30%, #2563eb 70%)' }} />
      <div style={{ position: 'absolute', inset: 0, ...overlayStyle, mixBlendMode: cfg.overlayBlendMode || 'normal' }} />
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 10, color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.5)', fontWeight: 600, letterSpacing: '0.04em' }}>Overlay Preview</span>
      </div>
    </div>
  );
}

// ─── Box shadow preview swatch ────────────────────────────────────────────────

function ShadowSwatch({ cfg }) {
  const shadowStr = cfg.boxShadow === 'custom'
    ? `${cfg.boxShadowX}px ${cfg.boxShadowY}px ${cfg.boxShadowBlur}px ${cfg.boxShadowSpread}px ${cfg.boxShadowColor}40`
    : cfg.boxShadow === 'sm'  ? '0 1px 4px rgba(0,0,0,0.12)'
    : cfg.boxShadow === 'md'  ? '0 4px 16px rgba(0,0,0,0.15)'
    : cfg.boxShadow === 'lg'  ? '0 8px 32px rgba(0,0,0,0.18)'
    : 'none';

  return (
    <div style={{ padding: '8px', background: '#f8fafc', borderRadius: 6, marginBottom: 8 }}>
      <div style={{ height: 36, background: '#fff', borderRadius: 6, boxShadow: shadowStr, border: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 10, color: '#94a3b8' }}>Shadow Preview</span>
      </div>
    </div>
  );
}

// ─── Image upload zone ────────────────────────────────────────────────────────

function ImageUploadZone({ value, onChange, label = 'Image' }) {
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);
  const { theme } = useTheme();

  const handleFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (e) => onChange(e.target.result);
    reader.readAsDataURL(file);
  };

  return (
    <div>
      {value && (
        <div style={{ position: 'relative', marginBottom: 8, borderRadius: 6, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
          <img src={value} alt="preview" style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }} />
          <button type="button" onClick={() => onChange('')}
            style={{ position: 'absolute', top: 4, right: 4, padding: '2px 8px', border: 'none', borderRadius: 4, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 11, cursor: 'pointer' }}>
            Remove
          </button>
        </div>
      )}
      <div
        onDragEnter={() => setDragging(true)}
        onDragLeave={() => setDragging(false)}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
        onClick={() => fileRef.current?.click()}
        style={{ border: `2px dashed ${dragging ? theme.primary : '#d1d5db'}`, borderRadius: 8, padding: '14px 10px', textAlign: 'center', background: dragging ? `${theme.primary}08` : '#f9fafb', cursor: 'pointer', marginBottom: 8, transition: 'border-color 0.15s, background 0.15s' }}
      >
        <i className="bi bi-cloud-upload" style={{ fontSize: 20, color: dragging ? theme.primary : '#94a3b8', display: 'block', marginBottom: 4 }} />
        <div style={{ fontSize: 11, color: '#64748b' }}>Drag & drop or <span style={{ color: theme.primary, fontWeight: 600 }}>browse</span></div>
        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>PNG, JPG, GIF, WebP, SVG</div>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
      </div>
      <Row label={`${label} URL`}>
        <div style={{ display: 'flex', gap: 4 }}>
          <input className="form-control form-control-sm" value={value || ''} onChange={e => onChange(e.target.value)}
            placeholder="https://…" style={{ fontSize: 12, flex: 1 }} />
          {value && (
            <a href={value} target="_blank" rel="noreferrer" style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 4, background: '#fff', color: '#64748b', fontSize: 12, display: 'flex', alignItems: 'center' }}>
              <i className="bi bi-box-arrow-up-right" />
            </a>
          )}
        </div>
      </Row>
    </div>
  );
}

// ─── Text overlay position picker ─────────────────────────────────────────────

const TEXT_POS_OPTIONS = [
  ['top-left','top-center','top-right'],
  ['center-left','centered','center-right'],
  ['bottom-left','bottom-center','bottom-right'],
];
const TEXT_POS_ICONS = {
  'top-left': '↖', 'top-center': '↑', 'top-right': '↗',
  'center-left': '←', 'centered': '✛', 'center-right': '→',
  'bottom-left': '↙', 'bottom-center': '↓', 'bottom-right': '↘',
};

function TextPositionGrid({ value, onChange }) {
  const { theme } = useTheme();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 3 }}>
      {TEXT_POS_OPTIONS.flat().map(pos => {
        const active = value === pos;
        return (
          <button key={pos} type="button" title={pos} onClick={() => onChange(pos)}
            style={{ height: 32, border: `2px solid ${active ? theme.primary : '#e2e8f0'}`, borderRadius: 4, background: active ? `${theme.primary}18` : '#f8fafc', cursor: 'pointer', fontSize: 14, color: active ? theme.primary : '#94a3b8', fontWeight: active ? 700 : 400 }}>
            {TEXT_POS_ICONS[pos]}
          </button>
        );
      })}
    </div>
  );
}

// ─── Border radius diagram ────────────────────────────────────────────────────

function BorderRadiusDiagram({ tl, tr, bl, br, onChange }) {
  return (
    <div style={{ position: 'relative', width: '100%', height: 90, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', marginBottom: 8 }}>
      <div style={{ position: 'absolute', inset: 10, background: '#e2e8f0', borderRadius: `${tl}px ${tr}px ${br}px ${bl}px` }} />
      {/* Corner inputs */}
      {[
        { key: 'tl', val: tl, style: { top: 2, left: 2 } },
        { key: 'tr', val: tr, style: { top: 2, right: 2 } },
        { key: 'bl', val: bl, style: { bottom: 2, left: 2 } },
        { key: 'br', val: br, style: { bottom: 2, right: 2 } },
      ].map(({ key, val, style }) => (
        <input key={key} type="number" value={val} min={0} max={100}
          onChange={e => onChange(key, Number(e.target.value))}
          style={{ position: 'absolute', ...style, width: 38, height: 22, fontSize: 10, textAlign: 'center', border: '1px solid #d1d5db', borderRadius: 3, background: '#fff', padding: '0 2px' }} />
      ))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ImageBannerEditor({ section, dispatch }) {
  const { theme } = useTheme();
  const cfg = { ...DEFAULT_IMAGE_BANNER_CONFIG, ...section.section_config };

  const [activeTab, setActiveTab] = useState('image');
  const [showPresets, setShowPresets] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const set = useCallback((patch) => {
    dispatch({ type: 'UPDATE_SECTION_CONFIG', clientId: section._clientId, patch });
  }, [dispatch, section._clientId]);

  const setPrimary = useCallback((patch) => {
    set({ primaryCTA: { ...cfg.primaryCTA, ...patch } });
  }, [set, cfg.primaryCTA]);

  const setSecondary = useCallback((patch) => {
    set({ secondaryCTA: { ...cfg.secondaryCTA, ...patch } });
  }, [set, cfg.secondaryCTA]);

  const applyPreset = (preset) => {
    const merged = { ...preset.config };
    CONTENT_KEYS.forEach(k => {
      if (cfg[k] !== undefined) merged[k] = cfg[k];
    });
    set(merged);
    setShowPresets(false);
  };

  const resetToDefault = () => set(DEFAULT_IMAGE_BANNER_CONFIG);

  const copyStyles = async () => {
    const styles = { ...cfg };
    CONTENT_KEYS.forEach(k => delete styles[k]);
    await navigator.clipboard.writeText(JSON.stringify(styles, null, 2)).catch(() => {});
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 1800);
  };

  const pasteStyles = async () => {
    try {
      const raw = await navigator.clipboard.readText();
      const styles = JSON.parse(raw);
      const safe = { ...styles };
      CONTENT_KEYS.forEach(k => delete safe[k]);
      set(safe);
    } catch {}
  };

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontSize: 12 }}>

      {/* ── Top action bar ── */}
      <div style={{ display: 'flex', gap: 6, padding: '0 0 12px', flexWrap: 'wrap' }}>
        <button type="button" onClick={() => setShowPresets(o => !o)}
          style={{ flex: 1, padding: '6px 10px', border: `1px solid ${theme.primary}40`, borderRadius: 6, background: showPresets ? `${theme.primary}12` : '#fff', color: theme.primary, fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'center' }}>
          <i className="bi bi-stars" /> Presets
        </button>
        <button type="button" onClick={copyStyles}
          style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', color: copySuccess ? '#10b981' : '#475569', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
          <i className={`bi ${copySuccess ? 'bi-check2' : 'bi-clipboard'}`} />{copySuccess ? 'Copied!' : 'Copy'}
        </button>
        <button type="button" onClick={pasteStyles}
          style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', color: '#475569', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
          <i className="bi bi-clipboard-check" /> Paste
        </button>
        <button type="button" onClick={resetToDefault}
          style={{ padding: '6px 10px', border: '1px solid #fee2e2', borderRadius: 6, background: '#fff', color: '#ef4444', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
          <i className="bi bi-arrow-counterclockwise" /> Reset
        </button>
      </div>

      {/* ── Presets grid ── */}
      {showPresets && (
        <div style={{ marginBottom: 12, padding: 10, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 8 }}>Choose a preset</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {PRESETS.map(p => (
              <button key={p.key} type="button" onClick={() => applyPreset(p)}
                style={{ padding: '8px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', textAlign: 'left' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = theme.primary}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                  <i className={`bi ${p.icon}`} style={{ fontSize: 12, color: theme.primary }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#1e293b' }}>{p.label}</span>
                </div>
                <div style={{ fontSize: 10, color: '#64748b', lineHeight: 1.3 }}>{p.description}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Tab bar ── */}
      <div style={{
        display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0',
        marginBottom: 14, overflowX: 'auto', flexShrink: 0,
      }}>
        {TABS.map(tab => (
          <button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)} style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '9px 14px',
            border: 'none',
            borderBottom: activeTab === tab.key ? `2px solid ${theme.primary}` : '2px solid transparent',
            marginBottom: -2,
            background: 'transparent',
            color: activeTab === tab.key ? theme.primary : '#64748b',
            fontWeight: activeTab === tab.key ? 700 : 400,
            fontSize: 11,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            transition: 'color 0.15s',
          }}>
            <i className={`bi ${tab.icon}`} style={{ fontSize: 12 }} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* TAB: Image — Image Source & Fit + Banner Dimensions                    */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'image' && (
        <>
          <SectionDivider label="IMAGE SOURCE & FIT" />
          <ImageUploadZone value={cfg.imageUrl} onChange={v => set({ imageUrl: v })} label="Image" />

          <Row label="Alt Text" hint="Describe the image for screen readers">
            <input className="form-control form-control-sm" value={cfg.imageAlt} onChange={e => set({ imageAlt: e.target.value })} placeholder="Describe the image…" style={{ fontSize: 12 }} />
          </Row>

          <Row label="Object Fit">
            <select className="form-select form-select-sm" value={cfg.imageFit} onChange={e => set({ imageFit: e.target.value })} style={{ fontSize: 12 }}>
              <option value="cover">Cover (fill & crop)</option>
              <option value="contain">Contain (show full image)</option>
              <option value="fill">Fill (stretch)</option>
              <option value="none">None (natural size)</option>
              <option value="scale-down">Scale Down</option>
            </select>
          </Row>

          <Row label="Image Position">
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>Quick pick</div>
                <ImagePositionGrid
                  value={cfg.imagePosition === 'custom' ? 'center' : cfg.imagePosition}
                  onSelect={pos => set({ imagePosition: pos })}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>Or custom %</div>
                <button type="button" onClick={() => set({ imagePosition: 'custom' })}
                  style={{ width: '100%', padding: '5px 8px', border: `1px solid ${cfg.imagePosition === 'custom' ? theme.primary : '#e2e8f0'}`, borderRadius: 5, background: cfg.imagePosition === 'custom' ? `${theme.primary}10` : '#f8fafc', color: cfg.imagePosition === 'custom' ? theme.primary : '#64748b', fontSize: 11, cursor: 'pointer', marginBottom: 6 }}>
                  Custom XY
                </button>
                {cfg.imagePosition === 'custom' && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                      <span style={{ fontSize: 10, color: '#94a3b8', width: 12 }}>X</span>
                      <RangeSlider value={cfg.imagePositionX} onChange={v => set({ imagePositionX: v })} min={0} max={100} unit="%" />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 10, color: '#94a3b8', width: 12 }}>Y</span>
                      <RangeSlider value={cfg.imagePositionY} onChange={v => set({ imagePositionY: v })} min={0} max={100} unit="%" />
                    </div>
                  </>
                )}
              </div>
            </div>
          </Row>

          <Row label="Repeat">
            <select className="form-select form-select-sm" value={cfg.imageRepeat} onChange={e => set({ imageRepeat: e.target.value })} style={{ fontSize: 12 }}>
              <option value="no-repeat">No Repeat</option>
              <option value="repeat">Repeat (tile)</option>
              <option value="repeat-x">Repeat X</option>
              <option value="repeat-y">Repeat Y</option>
            </select>
          </Row>

          <SectionDivider label="EFFECTS" />
          <InlineRow label="Parallax scroll">
            <Toggle value={cfg.imageParallax} onChange={v => set({ imageParallax: v })} />
          </InlineRow>
          {cfg.imageParallax && (
            <SubPanel>
              <Row label={`Speed — ${cfg.imageParallaxSpeed}`}>
                <RangeSlider value={cfg.imageParallaxSpeed} onChange={v => set({ imageParallaxSpeed: v })} min={0.1} max={1.0} step={0.05} />
              </Row>
            </SubPanel>
          )}
          <InlineRow label="Lazy load">
            <Toggle value={cfg.imageLazyLoad} onChange={v => set({ imageLazyLoad: v })} />
          </InlineRow>
          <InlineRow label="Hover zoom">
            <Toggle value={cfg.imageHoverZoom} onChange={v => set({ imageHoverZoom: v })} />
          </InlineRow>
          {cfg.imageHoverZoom && (
            <SubPanel>
              <Row label={`Scale — ${cfg.imageHoverZoomScale}×`}>
                <RangeSlider value={cfg.imageHoverZoomScale} onChange={v => set({ imageHoverZoomScale: v })} min={1.0} max={1.3} step={0.01} />
              </Row>
              <Row label={`Transition — ${cfg.imageHoverTransitionDuration}ms`}>
                <RangeSlider value={cfg.imageHoverTransitionDuration} onChange={v => set({ imageHoverTransitionDuration: v })} min={100} max={1000} step={50} unit="ms" />
              </Row>
            </SubPanel>
          )}

          <SectionDivider label="BANNER DIMENSIONS" />
          <Row label="Section Width">
            <Seg value={cfg.sectionWidth} onChange={v => set({ sectionWidth: v })} options={[
              { value: 'full-bleed', label: 'Full', icon: 'bi-fullscreen' },
              { value: 'contained', label: 'Contained', icon: 'bi-layout-text-window' },
              { value: 'narrow', label: 'Narrow', icon: 'bi-layout-text-window-reverse' },
            ]} />
          </Row>

          <Row label="Height">
            <Seg value={cfg.height} onChange={v => set({ height: v })} options={[
              { value: 'auto', label: 'Auto' },
              { value: 'viewport', label: '100vh' },
              { value: 'px', label: 'Fixed px' },
            ]} />
          </Row>
          {cfg.height === 'px' && (
            <SubPanel>
              <Row label="Height (desktop)"><NumInput value={cfg.heightPx} onChange={v => set({ heightPx: v })} min={40} max={1200} unit="px" /></Row>
              <Row label="Height (tablet)"><NumInput value={cfg.heightTablet} onChange={v => set({ heightTablet: v })} min={40} max={900} unit="px" /></Row>
              <Row label="Height (mobile)"><NumInput value={cfg.heightMobile} onChange={v => set({ heightMobile: v })} min={40} max={700} unit="px" /></Row>
            </SubPanel>
          )}

          <Row label="Aspect Ratio">
            <select className="form-select form-select-sm" value={cfg.aspectRatio} onChange={e => set({ aspectRatio: e.target.value })} style={{ fontSize: 12 }}>
              <option value="none">None (use height setting)</option>
              <option value="16:9">16:9 (Widescreen)</option>
              <option value="4:3">4:3 (Classic)</option>
              <option value="3:2">3:2 (Photo)</option>
              <option value="1:1">1:1 (Square)</option>
              <option value="21:9">21:9 (Ultrawide)</option>
              <option value="custom">Custom</option>
            </select>
          </Row>
          {cfg.aspectRatio === 'custom' && (
            <SubPanel>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 6, alignItems: 'center' }}>
                <NumInput value={cfg.customAspectRatioX} onChange={v => set({ customAspectRatioX: v })} min={1} max={100} />
                <span style={{ color: '#94a3b8', fontWeight: 700 }}>:</span>
                <NumInput value={cfg.customAspectRatioY} onChange={v => set({ customAspectRatioY: v })} min={1} max={100} />
              </div>
            </SubPanel>
          )}

          <InlineRow label="Max Width">
            <Toggle value={cfg.maxWidth !== 'none'} onChange={v => set({ maxWidth: v ? 'px' : 'none' })} />
          </InlineRow>
          {cfg.maxWidth !== 'none' && (
            <SubPanel>
              <Row label="Max Width"><NumInput value={cfg.maxWidthPx} onChange={v => set({ maxWidthPx: v })} min={200} max={3000} unit="px" /></Row>
            </SubPanel>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* TAB: Overlay — Overlay + Text Overlay                                  */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'overlay' && (
        <>
          <SectionDivider label="OVERLAY" />
          <InlineRow label="Enable overlay">
            <Toggle value={cfg.overlayVisible} onChange={v => set({ overlayVisible: v })} />
          </InlineRow>
          {cfg.overlayVisible && (
            <>
              <OverlaySwatch cfg={cfg} />
              <Row label="Type">
                <Seg value={cfg.overlayType} onChange={v => set({ overlayType: v })} options={[
                  { value: 'solid', label: 'Solid' },
                  { value: 'gradient', label: 'Gradient' },
                  { value: 'pattern', label: 'Pattern' },
                ]} />
              </Row>

              {cfg.overlayType === 'solid' && (
                <SubPanel>
                  <Row label="Color"><ColorField value={cfg.overlayColor} onChange={v => set({ overlayColor: v })} /></Row>
                  <Row label={`Opacity — ${cfg.overlayOpacity}%`}>
                    <RangeSlider value={cfg.overlayOpacity} onChange={v => set({ overlayOpacity: v })} min={0} max={100} unit="%" />
                  </Row>
                </SubPanel>
              )}

              {cfg.overlayType === 'gradient' && (
                <SubPanel>
                  <Row label="Type">
                    <Seg value={cfg.overlayGradientType} onChange={v => set({ overlayGradientType: v })} options={[
                      { value: 'linear', label: 'Linear' },
                      { value: 'radial', label: 'Radial' },
                    ]} />
                  </Row>
                  <Row label="From"><ColorField value={cfg.overlayGradientFrom} onChange={v => set({ overlayGradientFrom: v })} /></Row>
                  <Row label="To"><ColorField value={cfg.overlayGradientTo} onChange={v => set({ overlayGradientTo: v })} /></Row>
                  {cfg.overlayGradientType === 'linear' && (
                    <Row label={`Angle — ${cfg.overlayGradientAngle}°`}>
                      <RangeSlider value={cfg.overlayGradientAngle} onChange={v => set({ overlayGradientAngle: v })} min={0} max={360} unit="°" />
                    </Row>
                  )}
                  <Row label={`Opacity — ${cfg.overlayOpacity}%`}>
                    <RangeSlider value={cfg.overlayOpacity} onChange={v => set({ overlayOpacity: v })} min={0} max={100} unit="%" />
                  </Row>
                </SubPanel>
              )}

              {cfg.overlayType === 'pattern' && (
                <SubPanel>
                  <Row label="Color"><ColorField value={cfg.overlayColor} onChange={v => set({ overlayColor: v })} /></Row>
                  <Row label={`Opacity — ${cfg.overlayOpacity}%`}>
                    <RangeSlider value={cfg.overlayOpacity} onChange={v => set({ overlayOpacity: v })} min={0} max={100} unit="%" />
                  </Row>
                  <p style={{ fontSize: 10, color: '#94a3b8', margin: '4px 0 0', lineHeight: 1.4 }}>Pattern renders as a dot-grid texture over the image.</p>
                </SubPanel>
              )}

              <Row label="Blend Mode">
                <select className="form-select form-select-sm" value={cfg.overlayBlendMode} onChange={e => set({ overlayBlendMode: e.target.value })} style={{ fontSize: 12 }}>
                  {['normal','multiply','screen','overlay','darken','lighten','color-burn','soft-light'].map(m => (
                    <option key={m} value={m}>{m.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                  ))}
                </select>
              </Row>
            </>
          )}

          <SectionDivider label="TEXT OVERLAY" />
          <InlineRow label="Show text overlay">
            <Toggle value={cfg.textOverlayVisible} onChange={v => set({ textOverlayVisible: v })} />
          </InlineRow>
          {cfg.textOverlayVisible && (
            <>
              <Row label="Position">
                <TextPositionGrid value={cfg.textOverlayLayout} onChange={v => set({ textOverlayLayout: v })} />
              </Row>
              {cfg.textOverlayLayout === 'custom' && (
                <SubPanel>
                  <Row label={`X — ${cfg.textOverlayPositionX}%`}>
                    <RangeSlider value={cfg.textOverlayPositionX} onChange={v => set({ textOverlayPositionX: v })} min={0} max={100} unit="%" />
                  </Row>
                  <Row label={`Y — ${cfg.textOverlayPositionY}%`}>
                    <RangeSlider value={cfg.textOverlayPositionY} onChange={v => set({ textOverlayPositionY: v })} min={0} max={100} unit="%" />
                  </Row>
                </SubPanel>
              )}

              <Row label="Text Alignment">
                <Seg value={cfg.textAlignment} onChange={v => set({ textAlignment: v })} options={[
                  { value: 'left', icon: 'bi-text-left', label: 'Left' },
                  { value: 'center', icon: 'bi-text-center', label: 'Center' },
                  { value: 'right', icon: 'bi-text-right', label: 'Right' },
                ]} />
              </Row>

              <SectionDivider label="BADGE" />
              <InlineRow label="Show badge">
                <Toggle value={cfg.badgeVisible} onChange={v => set({ badgeVisible: v })} />
              </InlineRow>
              {cfg.badgeVisible && (
                <SubPanel>
                  <Row label="Text"><input className="form-control form-control-sm" value={cfg.badgeText} onChange={e => set({ badgeText: e.target.value })} style={{ fontSize: 12 }} /></Row>
                  <Row label="Color"><ColorField value={cfg.badgeColor} onChange={v => set({ badgeColor: v })} /></Row>
                </SubPanel>
              )}

              <SectionDivider label="HEADLINE" />
              <Row label="Text">
                <input className="form-control form-control-sm" value={cfg.headlineText} onChange={e => set({ headlineText: e.target.value })} style={{ fontSize: 12 }} />
              </Row>
              <Row label="Font Family"><FontPicker value={cfg.headlineFontFamily} onChange={v => set({ headlineFontFamily: v })} /></Row>
              <Row label="Font Weight"><WeightSelector value={cfg.headlineFontWeight} onChange={v => set({ headlineFontWeight: v })} /></Row>
              <Row label={`Font Size — ${cfg.headlineFontSize}px`}>
                <RangeSlider value={cfg.headlineFontSize} onChange={v => set({ headlineFontSize: v })} min={14} max={120} unit="px" />
              </Row>
              <Row label="Color"><ColorField value={cfg.headlineColor} onChange={v => set({ headlineColor: v })} /></Row>

              <SectionDivider label="SUBHEADLINE" />
              <InlineRow label="Show subheadline">
                <Toggle value={cfg.subheadlineVisible} onChange={v => set({ subheadlineVisible: v })} />
              </InlineRow>
              {cfg.subheadlineVisible && (
                <SubPanel>
                  <Row label="Text"><textarea className="form-control form-control-sm" rows={2} value={cfg.subheadlineText} onChange={e => set({ subheadlineText: e.target.value })} style={{ fontSize: 12 }} /></Row>
                  <Row label={`Size — ${cfg.subheadlineFontSize}px`}>
                    <RangeSlider value={cfg.subheadlineFontSize} onChange={v => set({ subheadlineFontSize: v })} min={12} max={48} unit="px" />
                  </Row>
                  <Row label="Color"><ColorField value={cfg.subheadlineColor} onChange={v => set({ subheadlineColor: v })} /></Row>
                </SubPanel>
              )}

              <SectionDivider label="TEXT BOX" />
              <Row label="Max Width"><NumInput value={cfg.textMaxWidth} onChange={v => set({ textMaxWidth: v })} min={100} max={1400} unit="px" /></Row>
              <Row label="Padding"><NumInput value={cfg.textPadding} onChange={v => set({ textPadding: v })} min={0} max={80} unit="px" /></Row>
              <InlineRow label="Text shadow">
                <Toggle value={cfg.textShadow} onChange={v => set({ textShadow: v })} />
              </InlineRow>
              {cfg.textShadow && (
                <SubPanel>
                  <Row label="Shadow Color"><ColorField value={cfg.textShadowColor} onChange={v => set({ textShadowColor: v })} /></Row>
                  <Row label={`Blur — ${cfg.textShadowBlur}px`}>
                    <RangeSlider value={cfg.textShadowBlur} onChange={v => set({ textShadowBlur: v })} min={0} max={40} unit="px" />
                  </Row>
                </SubPanel>
              )}
            </>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* TAB: Buttons — CTA Buttons + Link & Interaction                        */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'buttons' && (
        <>
          <SectionDivider label="CTA BUTTONS" />
          <InlineRow label="Show CTA">
            <Toggle value={cfg.ctaVisible} onChange={v => set({ ctaVisible: v })} />
          </InlineRow>
          {cfg.ctaVisible && (
            <>
              <SectionDivider label="PRIMARY BUTTON" />
              <Row label="Label"><input className="form-control form-control-sm" value={cfg.primaryCTA.label} onChange={e => setPrimary({ label: e.target.value })} style={{ fontSize: 12 }} /></Row>
              <Row label="URL"><input className="form-control form-control-sm" value={cfg.primaryCTA.url} onChange={e => setPrimary({ url: e.target.value })} placeholder="/sign-up" style={{ fontSize: 12 }} /></Row>
              <InlineRow label="Open in new tab"><Toggle value={cfg.primaryCTA.openNewTab} onChange={v => setPrimary({ openNewTab: v })} /></InlineRow>
              <Row label="Style">
                <Seg value={cfg.primaryCTA.style} onChange={v => setPrimary({ style: v })} options={[
                  { value: 'filled', label: 'Filled' },
                  { value: 'outlined', label: 'Outlined' },
                  { value: 'ghost', label: 'Ghost' },
                ]} />
              </Row>
              <Row label="Size">
                <Seg value={cfg.primaryCTA.size} onChange={v => setPrimary({ size: v })} options={[
                  { value: 'sm', label: 'SM' },
                  { value: 'md', label: 'MD' },
                  { value: 'lg', label: 'LG' },
                ]} />
              </Row>
              <Row label="BG Color"><ColorField value={cfg.primaryCTA.color} onChange={v => setPrimary({ color: v })} /></Row>
              <Row label="Text Color"><ColorField value={cfg.primaryCTA.textColor} onChange={v => setPrimary({ textColor: v })} /></Row>

              <SectionDivider label="SECONDARY BUTTON" />
              <InlineRow label="Show secondary">
                <Toggle value={cfg.secondaryCTA.visible} onChange={v => setSecondary({ visible: v })} />
              </InlineRow>
              {cfg.secondaryCTA.visible && (
                <SubPanel>
                  <Row label="Label"><input className="form-control form-control-sm" value={cfg.secondaryCTA.label} onChange={e => setSecondary({ label: e.target.value })} style={{ fontSize: 12 }} /></Row>
                  <Row label="URL"><input className="form-control form-control-sm" value={cfg.secondaryCTA.url} onChange={e => setSecondary({ url: e.target.value })} placeholder="#" style={{ fontSize: 12 }} /></Row>
                  <InlineRow label="Open in new tab"><Toggle value={cfg.secondaryCTA.openNewTab} onChange={v => setSecondary({ openNewTab: v })} /></InlineRow>
                  <Row label="Style">
                    <Seg value={cfg.secondaryCTA.style} onChange={v => setSecondary({ style: v })} options={[
                      { value: 'filled', label: 'Filled' },
                      { value: 'outlined', label: 'Outlined' },
                      { value: 'ghost', label: 'Ghost' },
                      { value: 'link', label: 'Link' },
                    ]} />
                  </Row>
                  <Row label="Size">
                    <Seg value={cfg.secondaryCTA.size} onChange={v => setSecondary({ size: v })} options={[
                      { value: 'sm', label: 'SM' },
                      { value: 'md', label: 'MD' },
                      { value: 'lg', label: 'LG' },
                    ]} />
                  </Row>
                  <Row label="BG Color"><ColorField value={cfg.secondaryCTA.color} onChange={v => setSecondary({ color: v })} /></Row>
                  <Row label="Text Color"><ColorField value={cfg.secondaryCTA.textColor} onChange={v => setSecondary({ textColor: v })} /></Row>
                </SubPanel>
              )}

              <SectionDivider label="LAYOUT" />
              <Row label="Button Layout">
                <Seg value={cfg.ctaLayout} onChange={v => set({ ctaLayout: v })} options={[
                  { value: 'side-by-side', label: 'Side by side' },
                  { value: 'stacked', label: 'Stacked' },
                ]} />
              </Row>
              <Row label="Margin Top"><NumInput value={cfg.ctaMarginTop} onChange={v => set({ ctaMarginTop: v })} min={0} max={80} unit="px" /></Row>
            </>
          )}

          <SectionDivider label="LINK & INTERACTION" />
          <Row label="Banner Link URL" hint="Makes the entire banner clickable">
            <div style={{ display: 'flex', gap: 4 }}>
              <input className="form-control form-control-sm" value={cfg.bannerLinkUrl} onChange={e => set({ bannerLinkUrl: e.target.value })} placeholder="https://… or /path" style={{ fontSize: 12 }} />
              {cfg.bannerLinkUrl && (
                <a href={cfg.bannerLinkUrl} target="_blank" rel="noreferrer" style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 4, background: '#fff', color: '#64748b', fontSize: 12, display: 'flex', alignItems: 'center' }}>
                  <i className="bi bi-box-arrow-up-right" />
                </a>
              )}
            </div>
          </Row>
          {cfg.bannerLinkUrl && (
            <InlineRow label="Open in new tab">
              <Toggle value={cfg.bannerLinkOpenNewTab} onChange={v => set({ bannerLinkOpenNewTab: v })} />
            </InlineRow>
          )}

          <Row label="Cursor Style">
            <Seg value={cfg.cursorStyle} onChange={v => set({ cursorStyle: v })} options={[
              { value: 'default', label: 'Default' },
              { value: 'pointer', label: 'Pointer' },
              { value: 'zoom-in', label: 'Zoom' },
            ]} />
          </Row>

          <InlineRow label="Lightbox on click" hint="Opens full image in modal">
            <Toggle value={cfg.lightboxEnabled} onChange={v => set({ lightboxEnabled: v })} />
          </InlineRow>

          <SectionDivider label="HOVER EFFECTS" />
          <Row label={`Hover Overlay — ${cfg.hoverOverlayOpacity}%`}>
            <RangeSlider value={cfg.hoverOverlayOpacity} onChange={v => set({ hoverOverlayOpacity: v })} min={0} max={80} unit="%" />
          </Row>
          {cfg.hoverOverlayOpacity > 0 && (
            <SubPanel>
              <Row label="Color"><ColorField value={cfg.hoverOverlayColor} onChange={v => set({ hoverOverlayColor: v })} /></Row>
            </SubPanel>
          )}
          <Row label={`Hover Scale — ${cfg.hoverScale}×`}>
            <RangeSlider value={cfg.hoverScale} onChange={v => set({ hoverScale: v })} min={1.0} max={1.1} step={0.005} />
          </Row>
          <Row label={`Transition — ${cfg.hoverTransitionDuration}ms`}>
            <RangeSlider value={cfg.hoverTransitionDuration} onChange={v => set({ hoverTransitionDuration: v })} min={100} max={800} step={50} unit="ms" />
          </Row>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* TAB: Style — Border & Shape + Spacing & Layout + Caption & Credit      */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'style' && (
        <>
          <SectionDivider label="BORDER & SHAPE" />
          <InlineRow label="Border">
            <Toggle value={cfg.borderVisible} onChange={v => set({ borderVisible: v })} />
          </InlineRow>
          {cfg.borderVisible && (
            <SubPanel>
              <Row label="Color"><ColorField value={cfg.borderColor} onChange={v => set({ borderColor: v })} /></Row>
              <Row label={`Width — ${cfg.borderWidth}px`}>
                <RangeSlider value={cfg.borderWidth} onChange={v => set({ borderWidth: v })} min={1} max={12} unit="px" />
              </Row>
              <Row label="Style">
                <Seg value={cfg.borderStyle} onChange={v => set({ borderStyle: v })} options={[
                  { value: 'solid', label: 'Solid' },
                  { value: 'dashed', label: 'Dashed' },
                  { value: 'dotted', label: 'Dotted' },
                ]} />
              </Row>
            </SubPanel>
          )}

          <SectionDivider label="BORDER RADIUS" />
          <InlineRow label="Per corner">
            <Toggle value={cfg.borderRadiusPerCorner} onChange={v => set({ borderRadiusPerCorner: v })} />
          </InlineRow>
          {cfg.borderRadiusPerCorner ? (
            <>
              <BorderRadiusDiagram
                tl={cfg.borderRadiusTopLeft} tr={cfg.borderRadiusTopRight}
                bl={cfg.borderRadiusBottomLeft} br={cfg.borderRadiusBottomRight}
                onChange={(corner, val) => {
                  const map = { tl: 'borderRadiusTopLeft', tr: 'borderRadiusTopRight', bl: 'borderRadiusBottomLeft', br: 'borderRadiusBottomRight' };
                  set({ [map[corner]]: val });
                }}
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                <Row label="Top Left"><NumInput value={cfg.borderRadiusTopLeft} onChange={v => set({ borderRadiusTopLeft: v })} min={0} max={100} unit="px" /></Row>
                <Row label="Top Right"><NumInput value={cfg.borderRadiusTopRight} onChange={v => set({ borderRadiusTopRight: v })} min={0} max={100} unit="px" /></Row>
                <Row label="Bottom Left"><NumInput value={cfg.borderRadiusBottomLeft} onChange={v => set({ borderRadiusBottomLeft: v })} min={0} max={100} unit="px" /></Row>
                <Row label="Bottom Right"><NumInput value={cfg.borderRadiusBottomRight} onChange={v => set({ borderRadiusBottomRight: v })} min={0} max={100} unit="px" /></Row>
              </div>
            </>
          ) : (
            <Row label={`Radius — ${cfg.borderRadius}px`}>
              <RangeSlider value={cfg.borderRadius} onChange={v => set({ borderRadius: v })} min={0} max={80} unit="px" />
            </Row>
          )}

          <SectionDivider label="BOX SHADOW" />
          <Row label="Shadow Preset">
            <Seg value={cfg.boxShadow} onChange={v => set({ boxShadow: v })} options={[
              { value: 'none', label: 'None' },
              { value: 'sm', label: 'SM' },
              { value: 'md', label: 'MD' },
              { value: 'lg', label: 'LG' },
              { value: 'custom', label: 'Custom' },
            ]} />
          </Row>
          {cfg.boxShadow !== 'none' && <ShadowSwatch cfg={cfg} />}
          {cfg.boxShadow === 'custom' && (
            <SubPanel>
              <Row label="Color"><ColorField value={cfg.boxShadowColor} onChange={v => set({ boxShadowColor: v })} /></Row>
              <Row label={`X — ${cfg.boxShadowX}px`}><RangeSlider value={cfg.boxShadowX} onChange={v => set({ boxShadowX: v })} min={-50} max={50} unit="px" /></Row>
              <Row label={`Y — ${cfg.boxShadowY}px`}><RangeSlider value={cfg.boxShadowY} onChange={v => set({ boxShadowY: v })} min={-50} max={50} unit="px" /></Row>
              <Row label={`Blur — ${cfg.boxShadowBlur}px`}><RangeSlider value={cfg.boxShadowBlur} onChange={v => set({ boxShadowBlur: v })} min={0} max={100} unit="px" /></Row>
              <Row label={`Spread — ${cfg.boxShadowSpread}px`}><RangeSlider value={cfg.boxShadowSpread} onChange={v => set({ boxShadowSpread: v })} min={-50} max={50} unit="px" /></Row>
            </SubPanel>
          )}

          <SectionDivider label="SHAPE" />
          <Row label="Clip Path">
            <select className="form-select form-select-sm" value={cfg.clipPath} onChange={e => set({ clipPath: e.target.value })} style={{ fontSize: 12 }}>
              <option value="none">None</option>
              <option value="diagonal-left">Diagonal (left slant)</option>
              <option value="diagonal-right">Diagonal (right slant)</option>
              <option value="curve-bottom">Curve bottom</option>
              <option value="parallelogram">Parallelogram</option>
              <option value="custom">Custom CSS value</option>
            </select>
          </Row>
          {cfg.clipPath === 'custom' && (
            <Row label="Custom clip-path">
              <input className="form-control form-control-sm" value={cfg.clipPathCustomValue} onChange={e => set({ clipPathCustomValue: e.target.value })} placeholder="polygon(0 0, 100% 0, 90% 100%, 0 100%)" style={{ fontSize: 11, fontFamily: 'monospace' }} />
            </Row>
          )}

          <Row label="Shape Divider (bottom)">
            <select className="form-select form-select-sm" value={cfg.shapeDividerBottom} onChange={e => set({ shapeDividerBottom: e.target.value })} style={{ fontSize: 12 }}>
              <option value="none">None</option>
              <option value="wave">Wave</option>
              <option value="diagonal">Diagonal</option>
              <option value="curve">Curve</option>
              <option value="arrow">Arrow</option>
            </select>
          </Row>
          {cfg.shapeDividerBottom !== 'none' && (
            <SubPanel>
              <Row label="Color"><ColorField value={cfg.shapeDividerColor} onChange={v => set({ shapeDividerColor: v })} /></Row>
              <Row label={`Height — ${cfg.shapeDividerHeight}px`}>
                <RangeSlider value={cfg.shapeDividerHeight} onChange={v => set({ shapeDividerHeight: v })} min={20} max={200} unit="px" />
              </Row>
            </SubPanel>
          )}

          <SectionDivider label="SPACING & LAYOUT" />
          <SectionDivider label="MARGIN (outside)" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Row label="Top"><NumInput value={cfg.marginTop} onChange={v => set({ marginTop: v })} min={0} max={200} unit="px" /></Row>
            <Row label="Bottom"><NumInput value={cfg.marginBottom} onChange={v => set({ marginBottom: v })} min={0} max={200} unit="px" /></Row>
          </div>
          <SectionDivider label="PADDING (inside)" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Row label="Top"><NumInput value={cfg.paddingTop} onChange={v => set({ paddingTop: v })} min={0} max={200} unit="px" /></Row>
            <Row label="Bottom"><NumInput value={cfg.paddingBottom} onChange={v => set({ paddingBottom: v })} min={0} max={200} unit="px" /></Row>
            <Row label="Left"><NumInput value={cfg.paddingLeft} onChange={v => set({ paddingLeft: v })} min={0} max={200} unit="px" /></Row>
            <Row label="Right"><NumInput value={cfg.paddingRight} onChange={v => set({ paddingRight: v })} min={0} max={200} unit="px" /></Row>
          </div>

          <SectionDivider label="CAPTION & CREDIT" />
          <InlineRow label="Show caption">
            <Toggle value={cfg.captionVisible} onChange={v => set({ captionVisible: v })} />
          </InlineRow>
          {cfg.captionVisible && (
            <SubPanel>
              <Row label="Caption Text">
                <textarea className="form-control form-control-sm" rows={2} value={cfg.captionText} onChange={e => set({ captionText: e.target.value })} style={{ fontSize: 12 }} />
              </Row>
              <Row label="Position">
                <Seg value={cfg.captionPosition} onChange={v => set({ captionPosition: v })} options={[
                  { value: 'below', label: 'Below image' },
                  { value: 'overlay-bottom', label: 'Overlay' },
                ]} />
              </Row>
              <Row label={`Font Size — ${cfg.captionFontSize}px`}>
                <RangeSlider value={cfg.captionFontSize} onChange={v => set({ captionFontSize: v })} min={10} max={24} unit="px" />
              </Row>
              <Row label="Color"><ColorField value={cfg.captionColor} onChange={v => set({ captionColor: v })} /></Row>
              <Row label="Alignment">
                <Seg value={cfg.captionAlignment} onChange={v => set({ captionAlignment: v })} options={[
                  { value: 'left', icon: 'bi-text-left', label: 'L' },
                  { value: 'center', icon: 'bi-text-center', label: 'C' },
                  { value: 'right', icon: 'bi-text-right', label: 'R' },
                ]} />
              </Row>
            </SubPanel>
          )}

          <InlineRow label="Show credit line">
            <Toggle value={cfg.creditVisible} onChange={v => set({ creditVisible: v })} />
          </InlineRow>
          {cfg.creditVisible && (
            <SubPanel>
              <Row label="Credit Text">
                <input className="form-control form-control-sm" value={cfg.creditText} onChange={e => set({ creditText: e.target.value })} placeholder="Photo by Jane Doe / Unsplash" style={{ fontSize: 12 }} />
              </Row>
              <Row label="Credit URL">
                <input className="form-control form-control-sm" value={cfg.creditUrl} onChange={e => set({ creditUrl: e.target.value })} placeholder="https://unsplash.com/…" style={{ fontSize: 12 }} />
              </Row>
            </SubPanel>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* TAB: Effects — Animation + Responsive Settings                         */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'effects' && (
        <>
          <SectionDivider label="ANIMATION" />
          <Row label="Entrance Animation">
            <select className="form-select form-select-sm" value={cfg.entranceAnimation} onChange={e => set({ entranceAnimation: e.target.value })} style={{ fontSize: 12 }}>
              <option value="none">None</option>
              <option value="fade-in">Fade In</option>
              <option value="slide-up">Slide Up</option>
              <option value="slide-left">Slide Left</option>
              <option value="slide-right">Slide Right</option>
              <option value="zoom-in">Zoom In</option>
              <option value="blur-in">Blur In</option>
            </select>
          </Row>
          {cfg.entranceAnimation !== 'none' && (
            <SubPanel>
              <Row label="Trigger">
                <Seg value={cfg.animationTrigger} onChange={v => set({ animationTrigger: v })} options={[
                  { value: 'on-load', label: 'On Load' },
                  { value: 'on-scroll', label: 'On Scroll' },
                ]} />
              </Row>
              <Row label={`Delay — ${cfg.animationDelay}ms`}>
                <RangeSlider value={cfg.animationDelay} onChange={v => set({ animationDelay: v })} min={0} max={2000} step={100} unit="ms" />
              </Row>
              <Row label={`Duration — ${cfg.animationDuration}ms`}>
                <RangeSlider value={cfg.animationDuration} onChange={v => set({ animationDuration: v })} min={200} max={2000} step={100} unit="ms" />
              </Row>
            </SubPanel>
          )}

          <SectionDivider label="RESPONSIVE SETTINGS" />
          <InlineRow label="Show on desktop"><Toggle value={cfg.visibleOnDesktop} onChange={v => set({ visibleOnDesktop: v })} /></InlineRow>
          <InlineRow label="Show on tablet"><Toggle value={cfg.visibleOnTablet} onChange={v => set({ visibleOnTablet: v })} /></InlineRow>
          <InlineRow label="Show on mobile"><Toggle value={cfg.visibleOnMobile} onChange={v => set({ visibleOnMobile: v })} /></InlineRow>

          <SectionDivider label="ALTERNATE IMAGES" />
          <Row label="Tablet Image">
            <ImageUploadZone value={cfg.tabletImageUrl} onChange={v => set({ tabletImageUrl: v })} label="Tablet Image" />
          </Row>
          <Row label="Mobile Image">
            <ImageUploadZone value={cfg.mobileImageUrl} onChange={v => set({ mobileImageUrl: v })} label="Mobile Image" />
          </Row>
        </>
      )}

    </div>
  );
}
